/**
 * ResponseViewer Component
 * Displays detailed survey responses for reviewers
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';
import SurveyNotes from './SurveyNotes';

/**
 * Modal component to view full survey responses
 */
function ResponseViewer({ sessionId, onClose }) {
    const [surveyData, setSurveyData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('responses');

    useEffect(() => {
        if (sessionId) {
            loadFullSurvey();
        }
    }, [sessionId]);

    const loadFullSurvey = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('adminToken');
            const swaPrincipal = localStorage.getItem('swaPrincipal');
            const response = await fetch(
                `${API_BASE_URL}/api/admin/sessions/${sessionId}/full`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error('Failed to load survey details');
            }
            
            const data = await response.json();
            setSurveyData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'completed': return 'status-completed';
            case 'in_progress': return 'status-progress';
            default: return 'status-pending';
        }
    };

    // Group responses by base question
    const groupedResponses = () => {
        if (!surveyData?.responses) return [];
        
        const groups = [];
        let currentGroup = null;
        
        surveyData.responses.forEach(response => {
            if (!response.is_followup) {
                if (currentGroup) groups.push(currentGroup);
                currentGroup = {
                    baseQuestion: response,
                    followups: []
                };
            } else if (currentGroup) {
                currentGroup.followups.push(response);
            }
        });
        
        if (currentGroup) groups.push(currentGroup);
        return groups;
    };

    const exportToCSV = () => {
        if (!surveyData) return;
        
        const rows = [
            ['Question #', 'Question', 'Answer', 'Type', 'Timestamp']
        ];
        
        surveyData.responses.forEach(r => {
            rows.push([
                r.question_number,
                `"${r.question_text.replace(/"/g, '""')}"`,
                `"${r.answer_text.replace(/"/g, '""')}"`,
                r.is_followup ? 'Follow-up' : 'Base',
                formatDate(r.timestamp)
            ]);
        });
        
        const csv = rows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey-${surveyData.employee_name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!sessionId) return null;

    return (
        <div className="response-viewer-overlay" onClick={onClose}>
            <div className="response-viewer-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="response-viewer-header">
                    <div className="header-title">
                        <h2>📋 Survey Response Details</h2>
                        {surveyData && (
                            <span className={`status-badge ${getStatusClass(surveyData.status)}`}>
                                {surveyData.status.replace('_', ' ')}
                            </span>
                        )}
                    </div>
                    <div className="header-actions">
                        <button className="btn-export" onClick={exportToCSV} disabled={!surveyData}>
                            📥 Export CSV
                        </button>
                        <button className="btn-close" onClick={onClose}>×</button>
                    </div>
                </div>

                {/* Content */}
                <div className="response-viewer-content">
                    {isLoading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading survey details...</p>
                        </div>
                    ) : error ? (
                        <div className="error-state">
                            <p>⚠️ {error}</p>
                            <button onClick={loadFullSurvey}>Retry</button>
                        </div>
                    ) : surveyData && (
                        <>
                            {/* Employee Info Card */}
                            <div className="employee-card">
                                <div className="employee-avatar">
                                    {surveyData.employee_name.charAt(0).toUpperCase()}
                                </div>
                                <div className="employee-details">
                                    <h3>{surveyData.employee_name}</h3>
                                    <p className="employee-role">{surveyData.role_title}</p>
                                    <p className="employee-meta">
                                        <span>📧 {surveyData.employee_email}</span>
                                        <span>🏢 {surveyData.department}</span>
                                        {surveyData.employee_id && <span>🆔 {surveyData.employee_id}</span>}
                                    </p>
                                </div>
                                <div className="survey-stats">
                                    <div className="stat">
                                        <span className="stat-value">{surveyData.responses?.length || 0}</span>
                                        <span className="stat-label">Responses</span>
                                    </div>
                                    {surveyData.progress && (
                                        <div className="stat">
                                            <span className="stat-value">{Math.round(surveyData.progress.percent_complete)}%</span>
                                            <span className="stat-label">Complete</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Timeline Info */}
                            <div className="timeline-info">
                                <div className="timeline-item">
                                    <span className="label">Created:</span>
                                    <span className="value">{formatDate(surveyData.created_at)}</span>
                                    {surveyData.created_by && <span className="by">by {surveyData.created_by}</span>}
                                </div>
                                <div className="timeline-item">
                                    <span className="label">Started:</span>
                                    <span className="value">{formatDate(surveyData.started_at)}</span>
                                </div>
                                <div className="timeline-item">
                                    <span className="label">Completed:</span>
                                    <span className="value">{formatDate(surveyData.completed_at)}</span>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="viewer-tabs">
                                <button 
                                    className={activeTab === 'responses' ? 'active' : ''}
                                    onClick={() => setActiveTab('responses')}
                                >
                                    💬 Responses ({surveyData.responses?.length || 0})
                                </button>
                                {surveyData.pulse_feedback && (
                                    <button 
                                        className={activeTab === 'pulse' ? 'active' : ''}
                                        onClick={() => setActiveTab('pulse')}
                                    >
                                        📊 Pulse Feedback
                                    </button>
                                )}
                                <button 
                                    className={activeTab === 'notes' ? 'active' : ''}
                                    onClick={() => setActiveTab('notes')}
                                >
                                    📝 Team Notes
                                </button>
                                {surveyData.job_description && (
                                    <button 
                                        className={activeTab === 'job_description' ? 'active' : ''}
                                        onClick={() => setActiveTab('job_description')}
                                    >
                                        📄 Job Description
                                    </button>
                                )}
                            </div>

                            {/* Tab Content */}
                            <div className="tab-content">
                                {activeTab === 'responses' && (
                                    <div className="responses-list">
                                        {surveyData.responses?.length === 0 ? (
                                            <div className="empty-responses">
                                                <p>No responses yet. Survey has not been started.</p>
                                            </div>
                                        ) : (
                                            groupedResponses().map((group, index) => (
                                                <div key={index} className="response-group">
                                                    {/* Base Question */}
                                                    <div className="response-item base-question">
                                                        <div className="question-header">
                                                            <span className="question-number">
                                                                Q{group.baseQuestion.question_number}
                                                            </span>
                                                            <span className="question-type">Base Question</span>
                                                        </div>
                                                        <div className="question-text">
                                                            {group.baseQuestion.question_text}
                                                        </div>
                                                        <div className="answer-text">
                                                            {group.baseQuestion.answer_text}
                                                        </div>
                                                        <div className="response-timestamp">
                                                            {formatDate(group.baseQuestion.timestamp)}
                                                        </div>
                                                    </div>

                                                    {/* Follow-up Questions */}
                                                    {group.followups.map((followup, fIndex) => (
                                                        <div key={fIndex} className="response-item followup-question">
                                                            <div className="question-header">
                                                                <span className="question-number">
                                                                    ↳ Follow-up
                                                                </span>
                                                                <span className="question-type">AI Follow-up</span>
                                                            </div>
                                                            <div className="question-text">
                                                                {followup.question_text}
                                                            </div>
                                                            <div className="answer-text">
                                                                {followup.answer_text}
                                                            </div>
                                                            <div className="response-timestamp">
                                                                {formatDate(followup.timestamp)}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {activeTab === 'job_description' && surveyData.job_description && (
                                    <div className="job-description-content">
                                        <pre>{surveyData.job_description}</pre>
                                    </div>
                                )}

                                {activeTab === 'pulse' && surveyData.pulse_feedback && (
                                    <div className="pulse-feedback-content">
                                        <div className="pulse-submitted-info">
                                            Submitted: {formatDate(surveyData.pulse_feedback.submitted_at)}
                                        </div>
                                        {surveyData.pulse_feedback.responses && surveyData.pulse_feedback.responses.length > 0 ? (
                                            <div className="pulse-responses-list">
                                                {surveyData.pulse_feedback.responses.map((r, i) => {
                                                    const question = surveyData.pulse_feedback.questions?.find(
                                                        q => q.id === r.question_id
                                                    );
                                                    return (
                                                        <div key={i} className="pulse-response-item">
                                                            <div className="pulse-question-text">
                                                                {question?.question || question?.text || r.question_id}
                                                            </div>
                                                            <div className={`pulse-answer ${r.answer === 'yes' ? 'pulse-yes' : 'pulse-no'}`}>
                                                                {r.answer === 'yes' ? '✅ Yes' : '❌ No'}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="empty-responses">No pulse responses recorded.</p>
                                        )}
                                        {surveyData.pulse_feedback.wants_to_talk && (
                                            <div className="pulse-talk-section">
                                                <div className="pulse-talk-header">🗣️ Wants to Talk</div>
                                                {surveyData.pulse_feedback.talk_topic && (
                                                    <div className="pulse-talk-topic">
                                                        Topic: {surveyData.pulse_feedback.talk_topic}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'notes' && (
                                    <div className="notes-tab-content">
                                        <SurveyNotes 
                                            sessionId={sessionId}
                                            employeeName={surveyData.employee_name}
                                            referenceType="all"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ResponseViewer;
